import React, {
  FC,
  useState,
  useEffect,
  useMemo
} from 'react';
import clsx from 'clsx';
import { Typography } from '@npm-registry/eapteka-ui';
import { getSizeStyles } from './helpers/getSizeStyles';
import { CountdownTimerProps } from './types';
import styles from './CountdownTimer.scss';

export const CountdownTimer: FC<CountdownTimerProps> = ({
  className,
  strokeBgColor = 'rgba(255, 255, 255, .5)',
  strokeColor = '#fff',
  strokeWidth = 2,
  textColor = '#fff',
  size = 56,
  milliseconds,
  callback
}) => {
  const [countdown, setCountdown] = useState(milliseconds);
  const [intervalId, setIntervalId] = useState(0);
  const [isPlaying, setPlaying] = useState(true);

  const rootClassName = useMemo(
    () =>
      clsx([styles.root, className], {
        [styles.rootIsAnimated]: isPlaying
      }),
    [isPlaying, className]
  );
  const radius = size / 2;
  const circumference = size * Math.PI;
  const countdownSizeStyles = getSizeStyles({
    size,
    strokeWidth,
    milliseconds,
    circumference
  });
  const seconds = useMemo(
    () => countdown / 1000,
    [countdown]
  );

  useEffect(() => {
    setPlaying(true);

    const interval = setInterval(() => {
      setCountdown((prevCountdown) => prevCountdown - 1000);
    }, 1000);

    if (typeof interval === 'number') {
      setIntervalId(interval);
    }

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      clearInterval(intervalId);
      setCountdown(0);
      setPlaying(false);
      callback();
    }
  }, [countdown, callback, intervalId]);

  return (
    <div
      className={rootClassName}
      style={countdownSizeStyles}
    >
      <Typography
        className={styles.seconds}
        style={{ color: textColor }}
      >
        {seconds}
      </Typography>
      <svg className={styles.svg}>
        <circle
          className={styles.mainCircle}
          cx={radius}
          cy={radius}
          r={radius}
          fill="none"
          stroke={strokeBgColor}
          opacity={0.6}
          strokeWidth={strokeWidth / 2}
        ></circle>
      </svg>
      {isPlaying && (
        <svg className={styles.svg}>
          <circle
            className={styles.circle}
            strokeDasharray={circumference}
            r={radius}
            cx={radius}
            cy={radius}
            fill="none"
            strokeLinecap="round"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          ></circle>
        </svg>
      )}
    </div>
  );
};
